import React, { memo, useCallback, useEffect, useState } from 'react';
import * as GStyled from '../../../styles/components';
import * as Styled from './styled';
import { ReportSnapshot, ReportType } from '../../../models';
import ReportSetting from '../report-setting/ReportSetting';
import ReportIncludedFields from '../report-included-fields/ReportIncludedFields';
import { useRouter } from 'next/router';
import { StyledText } from '@leasequery/lq.react-component-library';
import {
  fetchReportTypeSettingWithCache,
  fetchSavedReports,
  fetchSingleStandardReportWithCache,
  ReportSettingPayload,
  SavedReport,
} from '../../../api';
import { useAuth } from '../../../context';
import { noop } from '../../../util';
import SaveReportCriteriaDialog from '../../create-report/save-criteria-dialog/SaveReportCriteriaDialog';
import { saveReportCriteria } from '../../create-report/helpers/saveReportCriteria';
import { createRuleBuilderDefaultFields } from '../../helpers';
import { fetchReportDefaultFieldsWithCache } from '../../../api/report/fetchReportDefaultFields';
import { RuleBuilderField } from '../../create-report/rule-builder';
import { createReportSettingPayload } from '../../find-report/helpers/createRunReportPayload';
import { ReportSettingState } from '../../find-report/report-setting';
import { createReportSettingStateFromReportSnapshot } from '../../find-report/report-setting/helpers/createReportSettingStateFromReportSnapshot';
import {
  ReportNotifications,
  Dialog,
  LoadingSkeleton,
  Icon,
} from '../../../components';

interface ReportSummaryProps {
  reportSnapshot: ReportSnapshot;
}

function ReportSummary({ reportSnapshot }: ReportSummaryProps): JSX.Element {
  const router = useRouter();
  const { user, client } = useAuth();
  const [isFormLocked, setIsFormLocked] = useState(false);

  // TODO: report description not coming back from report snapshot api
  // const [reportDescription, setReportDescription] = useState('');
  const [isSaveCriteriaError, setIsSaveCriteriaError] = useState(false);

  const [showNotification, setShowNotification] = useState(false);

  const [saveCriteriaDialogIsOpen, setSaveCriteriaDialogIsOpen] = useState(
    false,
  );

  const [savedReports, setSavedReports] = useState([] as SavedReport[]);

  const [savedReportName, setSavedReportName] = useState<string>();

  const clientId = client ? client.clientId : user.clientId;

  const { standardReportTypeDisplay: reportType, id } = reportSnapshot || {};

  useEffect(() => {
    const handleGetSavedReport = async (id: number): Promise<void> => {
      const savedReports = await fetchSavedReports(id);

      const reportNameExists = savedReports.find(
        (x) => x.reportName === reportSnapshot?.reportName,
      );

      if (reportNameExists && reportNameExists.reportName !== savedReportName) {
        setSavedReportName(reportNameExists.reportName);
      }

      setSavedReports(savedReports);
    };

    if (reportSnapshot?.reportName) {
      handleGetSavedReport(clientId);
    }
  }, [clientId, reportSnapshot?.reportName, savedReportName]);

  const editCriteriaHandler = useCallback(() => {
    // if this is standard report, goto findReport page or goto create page as default
    router.push({
      pathname: reportType ? '/find' : '/create',
      query: { reportSnapshotId: id },
    });
  }, [id, reportType, router]);

  const reportCriteriaStateHandler = useCallback((state: boolean) => {
    setSaveCriteriaDialogIsOpen(state);
    setIsSaveCriteriaError(false);
  }, []);

  const loadReportFields = useCallback(async (): Promise<
    RuleBuilderField[]
  > => {
    const reportDefaultFields = await fetchReportDefaultFieldsWithCache(
      0,
      clientId,
    );

    const defaultFields = createRuleBuilderDefaultFields(
      reportDefaultFields,
      reportSnapshot,
    );

    return defaultFields;
  }, [reportSnapshot, clientId]);

  const handleNotificationClose = (): void => {
    setShowNotification(false);
  };

  const saveReportClickHandler = useCallback(
    async (
      reportName: string,
      reportDescription: string,
      isOverride: boolean,
    ) => {
      setIsFormLocked(true);

      let reportId = undefined;

      const reportNameExists = savedReports.find(
        (x) => x.reportName === reportName,
      );

      if (!isOverride && reportNameExists) {
        // ? if no bugs come back with removing this, delete the line
        // setSavedReportName(reportName);
        setIsFormLocked(false);
        setIsSaveCriteriaError(true);
        return;
      } else if (isOverride && reportNameExists) {
        reportId = reportNameExists.id;
      }

      const [fields, reportTypeSetting, standardReport] = await Promise.all([
        loadReportFields(),
        fetchReportTypeSettingWithCache(clientId),
        fetchSingleStandardReportWithCache(
          clientId,
          reportSnapshot.standardReportId,
        ),
      ]);
      const reportSettingState: ReportSettingState = createReportSettingStateFromReportSnapshot(
        reportSnapshot,
        reportTypeSetting,
      );
      const reportSetting: ReportSettingPayload = createReportSettingPayload(
        standardReport,
        reportSettingState,
      );

      await saveReportCriteria(
        reportName,
        reportDescription,
        client,
        fields,
        reportSetting,
        reportId,
      );

      setIsFormLocked(false);
      setSaveCriteriaDialogIsOpen(false);
      setShowNotification(true);

      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);

      return (): void => clearTimeout(timer);
    },
    [client, clientId, loadReportFields, reportSnapshot, savedReports],
  );

  return (
    <>
      <Styled.SectionHead>
        <Styled.SectionHeadTitleBar>
          <StyledText size="xl" fontWeight="medium">
            Report Summary
          </StyledText>
          {reportSnapshot &&
            reportSnapshot.standardReportTypeDisplay !==
              ReportType.disclosure &&
            reportSnapshot.reportName !== 'AP Report' && (
              // COR-19377 TODO: Allow Save for Reports with no Fields ex Complete Disclosure
              <>
                <GStyled.LinkButton
                  onClick={(): void => setSaveCriteriaDialogIsOpen(true)}
                >
                  <Icon name="saveArrow" />
                  &nbsp;Save Criteria
                </GStyled.LinkButton>
                <GStyled.LinkButton onClick={editCriteriaHandler}>
                  <Icon name="edit" />
                  &nbsp;Edit Criteria
                </GStyled.LinkButton>
              </>
            )}
        </Styled.SectionHeadTitleBar>
      </Styled.SectionHead>
      {reportSnapshot ? (
        <>
          <Styled.ReportSummarySubHead>
            <StyledText size="sm">Setting</StyledText>
            <StyledText size="sm">Included Fields</StyledText>
          </Styled.ReportSummarySubHead>
          <Styled.ReportSummaryBody>
            <ReportSetting reportSnapshot={reportSnapshot} />
            <ReportIncludedFields reportSnapshot={reportSnapshot} />
          </Styled.ReportSummaryBody>
        </>
      ) : (
        <LoadingSkeleton name="table" />
      )}
      <SaveReportCriteriaDialog
        isOpen={saveCriteriaDialogIsOpen}
        onSave={saveReportClickHandler}
        onStateChange={reportCriteriaStateHandler}
        isFormLocked={isFormLocked}
        isDuplicated={isSaveCriteriaError}
        savedReportName={savedReportName}
        savedReportDescription=""
      />
      <Dialog isOpen={showNotification} onStateChange={noop}>
        <ReportNotifications
          notificationType="saved"
          onClose={handleNotificationClose}
        />
      </Dialog>
    </>
  );
}

export default memo(ReportSummary);
